import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { LogOut, RefreshCw, Trash2, CheckCircle2, XCircle, Clock, ChevronRight, Phone, Calendar } from "lucide-react";
import Logo from "@/components/Logo";

const STATUSES = [
  { key: "all", label: "All" },
  { key: "pending", label: "Pending" },
  { key: "confirmed", label: "Confirmed" },
  { key: "completed", label: "Completed" },
  { key: "cancelled", label: "Cancelled" },
];

const STATUS_COLORS = {
  pending: { bg: "bg-[#f59e0b]/15", text: "text-[#f59e0b]", border: "border-[#f59e0b]/40" },
  confirmed: { bg: "bg-[#3b82f6]/15", text: "text-[#3b82f6]", border: "border-[#3b82f6]/40" },
  completed: { bg: "bg-[#10b981]/15", text: "text-[#10b981]", border: "border-[#10b981]/40" },
  cancelled: { bg: "bg-[#ef4444]/15", text: "text-[#ef4444]", border: "border-[#ef4444]/40" },
};

const StatusPill = ({ status }) => {
  const c = STATUS_COLORS[status] || STATUS_COLORS.pending;
  return (
    <span data-testid={`status-pill-${status}`} className={`inline-flex items-center gap-2 px-3 py-1 ${c.bg} ${c.text} border ${c.border} font-['Exo_2'] uppercase text-[10px] tracking-[0.2em] font-bold`}>
      {status}
    </span>
  );
};

const AdminDashboard = () => {
  const nav = useNavigate();
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ pending: 0, confirmed: 0, completed: 0, cancelled: 0, total: 0 });
  const [selected, setSelected] = useState(null);
  const user = localStorage.getItem("mda_user") || "admin";

  const logout = () => {
    localStorage.removeItem("mda_token");
    localStorage.removeItem("mda_user");
    nav("/admin/login");
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, s] = await Promise.all([
        api.get("/admin/appointments", { params: filter !== "all" ? { status_filter: filter } : {} }),
        api.get("/admin/appointments/stats"),
      ]);
      setList(a.data);
      setStats(s.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        logout();
      } else {
        toast.error("Failed to load appointments");
      }
    } finally {
      setLoading(false);
    }
  }, [filter]); // eslint-disable-line

  useEffect(() => {
    if (!localStorage.getItem("mda_token")) {
      nav("/admin/login");
      return;
    }
    load();
  }, [load, nav]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/appointments/${id}/status`, { status });
      toast.success(`Marked ${status}`);
      load();
      if (selected?.id === id) setSelected({ ...selected, status });
    } catch {
      toast.error("Update failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this appointment? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/appointments/${id}`);
      toast.success("Deleted");
      setSelected(null);
      load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const statCards = useMemo(() => ([
    { key: "total", label: "Total", value: stats.total, color: "text-white" },
    { key: "pending", label: "Pending", value: stats.pending, color: "text-[#f59e0b]" },
    { key: "confirmed", label: "Confirmed", value: stats.confirmed, color: "text-[#3b82f6]" },
    { key: "completed", label: "Completed", value: stats.completed, color: "text-[#10b981]" },
  ]), [stats]);

  return (
    <div data-testid="admin-dashboard-page" className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Header */}
      <header className="bg-black border-b border-[#2e2e2e] sticky top-0 z-40">
        <div className="px-6 lg:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo className="h-14 w-14 object-contain" />
            <div>
              <div className="font-['Anton'] text-chrome text-xl uppercase leading-none">Mid Del Auto</div>
              <div className="font-['Exo_2'] text-[9px] uppercase tracking-[0.3em] text-gray-500 mt-1">Admin · {user}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" data-testid="view-site-button" className="font-['Exo_2'] text-xs uppercase tracking-[0.3em] text-gray-400 hover:text-white">View Site</Link>
            <button data-testid="refresh-button" onClick={load} className="p-2 border border-[#2e2e2e] hover:border-[#e10600] transition-colors" title="Refresh">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button data-testid="logout-button" onClick={logout} className="inline-flex items-center gap-2 border border-[#2e2e2e] hover:border-[#e10600] px-4 py-2 font-['Exo_2'] uppercase text-[10px] tracking-[0.3em] font-bold transition-colors">
              <LogOut className="w-3 h-3" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="p-6 lg:p-10 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#2e2e2e] border border-[#2e2e2e]">
          {statCards.map((s) => (
            <div key={s.key} data-testid={`stat-${s.key}`} className="bg-[#0f0f0f] p-6">
              <div className="font-['Exo_2'] text-[10px] uppercase tracking-[0.3em] text-gray-500 font-bold mb-3">{s.label}</div>
              <div className={`font-['Anton'] text-5xl ${s.color}`}>{s.value}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-2">
          {STATUSES.map((st) => (
            <button key={st.key} data-testid={`filter-${st.key}`} onClick={() => setFilter(st.key)}
              className={`px-5 py-2 font-['Exo_2'] uppercase text-[10px] tracking-[0.3em] font-bold border transition-colors ${filter === st.key ? "bg-[#e10600] border-[#e10600] text-white" : "border-[#2e2e2e] text-gray-400 hover:text-white hover:border-white"}`}>
              {st.label} {st.key !== "all" && stats[st.key] !== undefined ? `(${stats[st.key]})` : ""}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="bg-[#0f0f0f] border border-[#2e2e2e] overflow-hidden">
          <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 bg-black border-b border-[#2e2e2e] font-['Exo_2'] text-[10px] uppercase tracking-[0.3em] text-gray-500 font-bold">
            <div className="col-span-3">Customer</div>
            <div className="col-span-3">Vehicle</div>
            <div className="col-span-2">Service</div>
            <div className="col-span-2">When</div>
            <div className="col-span-2 text-right">Status</div>
          </div>

          {loading && list.length === 0 && (
            <div data-testid="empty-loading" className="p-12 text-center font-['Exo_2'] text-gray-500 text-sm">Loading appointments…</div>
          )}
          {!loading && list.length === 0 && (
            <div data-testid="empty-state" className="p-12 text-center font-['Exo_2'] text-gray-500 text-sm">No appointments {filter !== "all" ? `in "${filter}"` : "yet"}.</div>
          )}

          {list.map((a) => (
            <button key={a.id} data-testid={`appointment-row-${a.id}`} onClick={() => setSelected(a)}
              className="w-full grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-6 py-5 border-b border-[#2e2e2e] last:border-b-0 hover:bg-[#141414] transition-colors text-left">
              <div className="md:col-span-3">
                <div className="font-['Exo_2'] text-white font-bold">{a.name}</div>
                <div className="font-['Exo_2'] text-gray-500 text-xs flex items-center gap-1 mt-1"><Phone className="w-3 h-3" /> {a.phone}</div>
              </div>
              <div className="md:col-span-3 font-['Exo_2'] text-gray-300 text-sm">{a.vehicle_year} {a.vehicle_make} {a.vehicle_model}</div>
              <div className="md:col-span-2 font-['Exo_2'] text-gray-300 text-sm">{a.service}</div>
              <div className="md:col-span-2 font-['Exo_2'] text-gray-300 text-sm flex items-center gap-1"><Calendar className="w-3 h-3" /> {a.preferred_date} · {a.preferred_time}</div>
              <div className="md:col-span-2 flex md:justify-end items-center gap-2">
                <StatusPill status={a.status} />
                <ChevronRight className="w-4 h-4 text-gray-600 hidden md:block" />
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Detail drawer */}
      {selected && (
        <div data-testid="appointment-detail-overlay" className="fixed inset-0 z-50 flex items-stretch justify-end bg-black/70" onClick={() => setSelected(null)}>
          <div data-testid="appointment-detail-panel" className="w-full max-w-lg bg-[#0a0a0a] border-l border-[#2e2e2e] h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-[#2e2e2e] flex items-center justify-between">
              <div>
                <div className="font-['Exo_2'] text-[10px] uppercase tracking-[0.3em] text-gray-500 font-bold">Appointment</div>
                <div className="font-['Anton'] text-white text-2xl uppercase mt-1">{selected.name}</div>
              </div>
              <button data-testid="close-detail-button" onClick={() => setSelected(null)} className="text-gray-500 hover:text-white">
                <XCircle className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 space-y-5">
              <div>
                <StatusPill status={selected.status} />
              </div>
              {[
                ["Phone", selected.phone],
                ["Email", selected.email || "—"],
                ["Vehicle", `${selected.vehicle_year} ${selected.vehicle_make} ${selected.vehicle_model}`],
                ["Service", selected.service],
                ["Date", selected.preferred_date],
                ["Time", selected.preferred_time],
                ["Notes", selected.notes || "—"],
              ].map(([k, v]) => (
                <div key={k} className="border-b border-[#2e2e2e] pb-3">
                  <div className="font-['Exo_2'] text-[10px] uppercase tracking-[0.3em] text-gray-500 font-bold mb-1">{k}</div>
                  <div className="font-['Exo_2'] text-white text-sm">{v}</div>
                </div>
              ))}

              <div className="pt-2 space-y-3">
                <div className="font-['Exo_2'] text-[10px] uppercase tracking-[0.3em] text-gray-500 font-bold">Update Status</div>
                <div className="grid grid-cols-2 gap-2">
                  <button data-testid="set-pending-button" onClick={() => updateStatus(selected.id, "pending")} className="inline-flex items-center justify-center gap-2 border border-[#f59e0b] text-[#f59e0b] hover:bg-[#f59e0b]/10 px-4 py-3 font-['Exo_2'] uppercase text-[10px] tracking-[0.3em] font-bold">
                    <Clock className="w-3 h-3" /> Pending
                  </button>
                  <button data-testid="set-confirmed-button" onClick={() => updateStatus(selected.id, "confirmed")} className="inline-flex items-center justify-center gap-2 border border-[#3b82f6] text-[#3b82f6] hover:bg-[#3b82f6]/10 px-4 py-3 font-['Exo_2'] uppercase text-[10px] tracking-[0.3em] font-bold">
                    <CheckCircle2 className="w-3 h-3" /> Confirm
                  </button>
                  <button data-testid="set-completed-button" onClick={() => updateStatus(selected.id, "completed")} className="inline-flex items-center justify-center gap-2 border border-[#10b981] text-[#10b981] hover:bg-[#10b981]/10 px-4 py-3 font-['Exo_2'] uppercase text-[10px] tracking-[0.3em] font-bold">
                    <CheckCircle2 className="w-3 h-3" /> Complete
                  </button>
                  <button data-testid="set-cancelled-button" onClick={() => updateStatus(selected.id, "cancelled")} className="inline-flex items-center justify-center gap-2 border border-[#ef4444] text-[#ef4444] hover:bg-[#ef4444]/10 px-4 py-3 font-['Exo_2'] uppercase text-[10px] tracking-[0.3em] font-bold">
                    <XCircle className="w-3 h-3" /> Cancel
                  </button>
                </div>

                <button data-testid="delete-appointment-button" onClick={() => remove(selected.id)} className="w-full inline-flex items-center justify-center gap-2 border border-[#2e2e2e] text-gray-500 hover:text-white hover:border-white px-4 py-3 font-['Exo_2'] uppercase text-[10px] tracking-[0.3em] font-bold mt-4">
                  <Trash2 className="w-3 h-3" /> Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
